// MY TIMESHEET - Employee Daily Entry Page
// Weekly timesheet view with daily entry cards and submission workflow.
// Phase 5: Daily Timesheet Entry

import React, { useState, useEffect, useContext, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { Row, Col, Card, Button, Modal, Form, Alert, Badge } from 'react-bootstrap'
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isToday,
  isFuture,
  parseISO,
} from 'date-fns'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { useAuth } from '../../modules/auth'
import roleContext from '../../context/roleContext'
import { RoleContextValue } from '../../types/OtherTypes'
import {
  getWeeklyTimesheet,
  createTimesheetEntry,
  updateTimesheetEntry,
  deleteTimesheetEntry,
  submitDailyTimesheet,
  getProjectCodesDropdown,
  getToolsDropdown,
  getTaskTypesDropdown,
  getToolsByProject,
  //getTaskTypesByTool,
  TimesheetEntry,
  TimesheetEntryInput,
  WeeklySummary,
} from '../../../api/TimesheetAPI'
import { getTasksByProject } from '../../../api/TaskAPI'
// Leave Approve so that day Add Entry was not show.
import { getLeaveEmails } from '../../../api/LeaveAPI'

const MyTimesheet = () => {
  const { currentUser } = useAuth()
  const roleState: RoleContextValue = useContext(roleContext)
  const userId = currentUser?.id || ''
  const isSuperAdmin =
    roleState.roleName
      ?.trim()
      .toUpperCase()
      .replace(/_/g, ' ') === 'SUPER ADMIN'

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  )

  // Data states
  const [weeklyData, setWeeklyData] = useState<WeeklySummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Form states
  const [showEntryModal, setShowEntryModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const isLoadingEditEntryRef = useRef(false)

  // Dropdown options
  const [projectCodes, setProjectCodes] = useState<any[]>([])
  const [tools, setTools] = useState<any[]>([])
  const [taskTypes, setTaskTypes] = useState<any[]>([])
  const [tasks, setTasks] = useState<any[]>([])
  const [allTools, setAllTools] = useState<any[]>([]) //Mater Tool
  const [allTaskTypes, setAllTaskTypes] = useState<any[]>([])

  // Form data
  const [formData, setFormData] = useState<Partial<TimesheetEntryInput>>({
    userId,
    date: format(new Date(), 'yyyy-MM-dd'),
    projectCodeId: '',
    taskId: null,
    toolId: '',
    taskTypeId: '',
    description: '',
    billableHours: 0,
    actualHours: 0,
    isOvertime: false,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
   // Leave Approve so that day Add Entry Button was not show
  const [approvedLeaves, setApprovedLeaves] = useState<any[]>([])


  // Load weekly data
  useEffect(() => {
    if (!userId) return // Wait for user to load
    loadWeeklyTimesheet()
    loadDropdownData()
    loadAllTools()    //Load all tools
    loadAllTaskTypes() 
    loadApprovedLeaves()  // Leave Approve so that day Add Entry Button was not show
  }, [currentWeekStart, userId])

  // Load tools and tasks when project changes (CASCADING LEVEL 1)
  // This effect only runs for NEW entries - edit mode loads data separately
  useEffect(() => {
    // Skip if in edit mode or loading edit entry - values are already set
    if (editingEntry || isLoadingEditEntryRef.current) return

    if (formData.projectCodeId) {
    const selectedProject = projectCodes.find(
      (p) => p.id === formData.projectCodeId
    )
      //  If Leave Project Selected
      if (selectedProject?.code === 'I0000') {
        // Show only Leave tool
        // const leaveTool = allTools.find(
        // (tool) => tool.name.toLowerCase().includes('leave')
      // )
      //setTools(leaveTools ? [leaveTools] : [])

    const leaveTools = allTools.filter((tool) =>
      tool.name.toLowerCase().includes('leave') ||
      tool.name.toLowerCase().includes('casual') ||
      tool.name.toLowerCase().includes('comp') ||
      tool.name.toLowerCase().includes('earned')
    )
    setTools(leaveTools)

      // Show only Leave related task types
      const leaveTaskTypes = taskTypes.filter((tt) =>
        ['Casual Leave', 'Comp Off', 'Earned Leave',].includes(tt.name)
      )

      setTaskTypes(leaveTaskTypes)
    } else {
      //  Normal project
      setTools(allTools)
      setTaskTypes(allTaskTypes)
      loadTasksForProject(formData.projectCodeId)
    }

    // Reset dependent fields
    setFormData((prev) => ({
      ...prev,
      toolId: '',
      taskTypeId: '',
      taskId: null,
    }))
    setTasks([])
  }
 else {
      // Reset all dependent fields if no project selected
      setTools([])
      //setTaskTypes([])
      setTasks([])
      setFormData((prev) => ({
        ...prev,
        toolId: '',
        //taskTypeId: '',
        taskId: null,
      }))
    }
  }, [formData.projectCodeId, editingEntry])

  // Load task types when tool changes (CASCADING LEVEL 2)
  // This effect only runs for NEW entries - edit mode loads data separately
  // useEffect(() => {
  //   // Skip if in edit mode or loading edit entry - values are already set
  //   if (editingEntry || isLoadingEditEntryRef.current) return

  //   if (formData.toolId) {
  //     // Load task types for this tool
  //     loadTaskTypesForTool(formData.toolId)

  //     // Reset task type selection
  //     setFormData((prev) => ({
  //       ...prev,
  //       taskTypeId: '',
  //     }))
  //   } else {
  //     // Reset task types if no tool selected
  //     setTaskTypes([])
  //     setFormData((prev) => ({
  //       ...prev,
  //       taskTypeId: '',
  //     }))
  //   }
  // }, [formData.toolId, editingEntry])

  const loadWeeklyTimesheet = async () => {
    try {
      setLoading(true)
      const response = await getWeeklyTimesheet(userId, format(currentWeekStart, 'yyyy-MM-dd'))
      if (response.data?.statusCode === 200 && response.data?.data) {
        setWeeklyData(response.data.data)
      }
    } catch (error: any) {
      console.error('Error loading weekly timesheet:', error)
      toast.error(error.response?.data?.message || 'Failed to load timesheet')
    } finally {
      setLoading(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      // Only load project codes initially
      // Tools and Task Types will be loaded based on selections
      const projectsRes = await getProjectCodesDropdown()

      if (projectsRes.data?.statusCode === 200 && projectsRes.data?.data) {
        setProjectCodes(projectsRes.data.data)
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error)
      toast.error('Failed to load dropdown options')
    }
  }

  //  Load ALL tools (MASTER list for new entries)
    const loadAllTools = async () => {
      try {
        const response = await getToolsDropdown()
        if (response.data?.statusCode === 200 && response.data?.data) {
          setAllTools(response.data.data || [])
          setTools(response.data.data || [])
        }
      } catch (error) {
        console.error('Error loading all tools:', error)
        toast.error('Failed to load tools')
      }
    }

    //  Load ALL Task Type (MASTER list for new entries)
    const loadAllTaskTypes = async () => {
      try {
        const response = await getTaskTypesDropdown()
        if (response.data?.statusCode === 200 && response.data?.data) {
          setTaskTypes(response.data.data || [])
          setAllTaskTypes(response.data.data || [])
        }
      } catch (error) {
        toast.error('Failed to load task types')
      }
    }


  const loadToolsForProject = async (projectId: string) => {
    try {
      const response = await getToolsByProject(projectId)
      if (response.data?.statusCode === 200 && response.data?.data) {
        setTools(response.data.data || [])
      } else {
        setTools([])
      }
    } catch (error) {
      console.error('Error loading tools:', error)
      toast.error('Failed to load tools for project')
      setTools([])
    }
  }

  // const loadTaskTypesForTool = async (toolId: string) => {
  //   try {
  //     const response = await getTaskTypesByTool(toolId)
  //     if (response.data?.statusCode === 200 && response.data?.data) {
  //       setTaskTypes(response.data.data || [])
  //     } else {
  //       setTaskTypes([])
  //     }
  //   } catch (error) {
  //     console.error('Error loading task types:', error)
  //     toast.error('Failed to load task types for tool')
  //     setTaskTypes([])
  //   }
  // }

  const loadTasksForProject = async (projectId: string) => {
    try {
      // Only load tasks assigned to the current user
      const response = await getTasksByProject(projectId, userId)
      if (response.data?.statusCode === 200 && response.data?.data) {
        setTasks(response.data.data || [])
      }
    } catch (error) {
      console.error('Error loading tasks:', error)
      toast.error('Failed to load tasks')
    }
  }
  // Leave Approve so that day Add Entry Button was not show.
  const loadApprovedLeaves = async () => {
  try {
    const response = await getLeaveEmails(userId, 'sender')

    if (response.statusCode === 200 && response.data) {
      const approved = response.data.filter(
        (leave: any) => leave.status === 'APPROVED'
      )

      setApprovedLeaves(approved)
    }
  } catch (error) {
    console.error('Error loading leaves:', error)
  }
}


  // Week navigation handlers
  const goToPreviousWeek = () => setCurrentWeekStart((prev) => subWeeks(prev, 1))
  const goToNextWeek = () => setCurrentWeekStart((prev) => addWeeks(prev, 1))
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))
  function isUserOnLeave(date: Date): boolean {
  const dateStr = format(date, 'yyyy-MM-dd')

  return approvedLeaves.some((leave: any) => {
    const start = format(parseISO(leave.startDate), 'yyyy-MM-dd')
    const end = format(parseISO(leave.endDate), 'yyyy-MM-dd')

    return dateStr >= start && dateStr <= end
  })
}


  // Open entry modal
  const openEntryModal = async (date: Date, entry?: TimesheetEntry) => {
    // 🚫 Prevent opening modal if user is on leave
    if (isUserOnLeave(date)) {
      toast.error("You are on leave for this day.")
      return
    }
    setSelectedDate(date)
    setEditingEntry(entry || null)

    if (entry) {
      // Set flag to prevent useEffect from resetting values during edit loading
      isLoadingEditEntryRef.current = true

      // Get IDs from entry - handle both direct ID and nested object cases
      const entryToolId = entry.toolId || entry.tool?.id || ''
      const entryTaskTypeId = entry.taskTypeId || entry.taskType?.id || ''
      const entryTaskId = entry.taskId || entry.task?.id || null

      // When editing, load the cascading dropdowns first
      // Load tools for the project
      if (entry.projectCodeId) {
        await loadToolsForProject(entry.projectCodeId)
      }

      // Load task types for the tool
      if (entryToolId) {
        //await loadTaskTypesForTool(entryToolId)
      }

      // Load tasks if it's a REAL project
      const selectedProject = projectCodes.find((p) => p.id === entry.projectCodeId)
      if (selectedProject?.type === 'REAL' && entry.projectCodeId) {
        await loadTasksForProject(entry.projectCodeId)
      }

      // Now set the form data with existing values
      setFormData({
        userId,
        date: format(parseISO(entry.date), 'yyyy-MM-dd'),
        projectCodeId: entry.projectCodeId,
        taskId: entryTaskId,
        toolId: entryToolId,
        taskTypeId: entryTaskTypeId,
        description: entry.description,
        billableHours: entry.billableHours,
        actualHours: entry.actualHours,
        isOvertime: entry.isOvertime,
      })

      setFormErrors({})
      setShowEntryModal(true)

      // Clear the flag after a short delay to ensure React has processed the state update
      // This allows the cascading dropdowns to work normally after initial edit load
      setTimeout(() => {
        isLoadingEditEntryRef.current = false
      }, 200)
    } else {
      // When creating new, reset everything
      setFormData({
        userId,
        date: format(date, 'yyyy-MM-dd'),
        projectCodeId: '',
        taskId: null,
        toolId: '',
        taskTypeId: '',
        description: '',
        billableHours: 0,
        actualHours: 0,
        isOvertime: false,
      })
      setTools(allTools)
      setTaskTypes(taskTypes)
      //setTools([])
      //setTaskTypes([])
      setTasks([])
      setFormErrors({})
      setShowEntryModal(true)
    }
  }

  const closeEntryModal = () => {
    setShowEntryModal(false)
    setEditingEntry(null)
    isLoadingEditEntryRef.current = false
    setFormData({
      userId,
      date: format(new Date(), 'yyyy-MM-dd'),
      projectCodeId: '',
      taskId: null,
      toolId: '',
      taskTypeId: '',
      description: '',
      billableHours: 0,
      actualHours: 0,
      isOvertime: false,
    })
    // Reset cascading dropdowns
    setTools([])
    //setTaskTypes([])
    setTasks([])
    setFormErrors({})
  }

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.projectCodeId) errors.projectCodeId = 'Project code is required'
    if (!formData.toolId) errors.toolId = 'Tool is required'
    if (!formData.taskTypeId) errors.taskTypeId = 'Task type is required'
    if (!formData.description || formData.description.trim() === '')
      errors.description = 'Description is required'
    if (formData.billableHours === undefined || formData.billableHours < 0)
      errors.billableHours = 'Billable hours must be 0 or greater'
    if (formData.billableHours! > 9) errors.billableHours = 'Billable hours cannot exceed 9'
    if (formData.actualHours === undefined || formData.actualHours <= 0)
      errors.actualHours = 'Actual hours must be greater than 0'
    if (formData.billableHours! > formData.actualHours!)
      errors.billableHours = 'Billable hours cannot exceed actual hours'

    // Check if real project requires task
    // const selectedProject = projectCodes.find((p) => p.id === formData.projectCodeId)
    // if (selectedProject?.type === 'REAL' && !formData.taskId) {
    //   errors.taskId = 'Task is required for real projects'
    // }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Save entry
  const handleSaveEntry = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors')
      return
    }

    try {
      setSubmitting(true)

      if (editingEntry) {
        // Update existing entry
        const response = await updateTimesheetEntry(editingEntry.id, formData)
        if (response.data?.statusCode === 200) {
          toast.success(response.data?.message || 'Timesheet entry updated successfully')
          closeEntryModal()
          loadWeeklyTimesheet()
        }
      } else {
        // Create new entry
        const response = await createTimesheetEntry(formData as TimesheetEntryInput)
        if (response.data?.statusCode === 201 || response.data?.statusCode === 200) {
          toast.success(response.data?.message || 'Timesheet entry created successfully')
          closeEntryModal()
          loadWeeklyTimesheet()
        }
      }
    } catch (error: any) {
      console.error('Error saving entry:', error)
      toast.error(error.response?.data?.message || 'Failed to save entry')
    } finally {
      setSubmitting(false)
    }
  }

  // Delete entry
  const handleDeleteEntry = async (entry: TimesheetEntry) => {
    const result = await Swal.fire({
      title: 'Delete Entry?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    })

    if (result.isConfirmed) {
      try {
        const response = await deleteTimesheetEntry(entry.id)
        if (response.data?.statusCode === 200 || response.data?.statusCode === 202) {
          toast.success(response.data?.message || 'Entry deleted successfully')
          loadWeeklyTimesheet()
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to delete entry')
      }
    }
  }

  // Submit daily timesheet
  const handleSubmitDaily = async (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    const entriesForDate = weeklyData?.entries.filter(
      (e) => format(parseISO(e.date), 'yyyy-MM-dd') === dateStr && e.status === 'DRAFT'
    )

    if (!entriesForDate || entriesForDate.length === 0) {
      toast.warning('No draft entries to submit for this date')
      return
    }

    const result = await Swal.fire({
      title: 'Submit Timesheet?',
      text: `Submit ${entriesForDate.length} ${entriesForDate.length === 1 ? 'entry' : 'entries'} for ${format(date, 'MMM dd, yyyy')}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit',
      cancelButtonText: 'Cancel',
    })

    if (result.isConfirmed) {
      try {
        const response = await submitDailyTimesheet(userId, dateStr)
        if (response.data?.statusCode === 200) {
          toast.success(response.data?.message || 'Timesheet submitted successfully')
          loadWeeklyTimesheet()
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to submit timesheet')
      }
    }
  }

  // Get entries for a specific date
  const getEntriesForDate = (date: Date): TimesheetEntry[] => {
    if (!weeklyData) return []
    const dateStr = format(date, 'yyyy-MM-dd')
    return weeklyData.entries.filter(
      (e) => format(parseISO(e.date), 'yyyy-MM-dd') === dateStr
    )
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'secondary'
      case 'SUBMITTED':
        return 'info'
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  // Early return if user not loaded
  if (!currentUser || !userId) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
        <div className='text-center'>
          <div className='spinner-border text-primary mb-3' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <p className='text-muted'>Loading user information...</p>
        </div>
      </div>
    )
  }

  // Render week days
  const renderWeekDays = () => {
    const weekDays: React.ReactElement[] = []
    for (let i = 0; i < 7; i++) {
      const date = addDays(currentWeekStart, i)
      const entries = getEntriesForDate(date)
      const isOnLeave = isUserOnLeave(date)  // Leave Approve so that day Add Entry Button was not show
      const hasDraftEntries = entries.some((e) => e.status === 'DRAFT')
      const isDateToday = isToday(date)
      const isFutureDate = isFuture(date) && !isToday(date)

      weekDays.push(
        <Col key={i} md={12} className='mb-4'>
          <Card className={`shadow-sm ${isDateToday ? 'border-primary' : ''}`}>
            <Card.Header className='d-flex justify-content-between align-items-center bg-light'>
              <div>
                <h5 className='mb-0'>
                  {format(date, 'EEEE')}
                  {isDateToday && (
                    <Badge bg='primary' className='ms-2'>
                      Today
                    </Badge>
                  )}
                </h5>
                <small className='text-muted'>{format(date, 'MMM dd, yyyy')}</small>
              </div>
              <div>
              {/* {!isFutureDate &&( */}
               {/* Leave Approve so that day Add Entry Button was not show */}
                {!isFutureDate && !isOnLeave && (
                  <>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      className='me-2'
                      onClick={() => openEntryModal(date)}
                    >
                      <i className='fas fa-plus me-1'></i>
                      Add Entry
                    </Button>
                    {hasDraftEntries && (
                      <Button
                        size='sm'
                        variant='success'
                        onClick={() => handleSubmitDaily(date)}
                      >
                        <i className='fas fa-paper-plane me-1'></i>
                        Submit
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card.Header>
            <Card.Body>
            {/* Leave Approve so that day Add Entry Button was not show */}
            {isOnLeave && (
                <Alert variant="danger" className="text-center">
                  You are on leave for this day.
                </Alert>
              )}
              {entries.length === 0 ? (
                <Alert variant='light' className='mb-0 text-center'>
                  <i className='fas fa-calendar-times me-2'></i>
                  No entries for this day
                </Alert>
              ) : (
                entries.map((entry) => (
                  <div
                    key={entry.id}
                    className='border rounded p-3 mb-2 hover-shadow'
                    style={{ cursor: 'pointer' }}
                  >
                    <div className='d-flex justify-content-between align-items-start'>
                      <div className='flex-grow-1'>
                        <h6 className='mb-1'>
                          {entry.projectCode?.code} - {entry.projectCode?.name}
                        </h6>
                        <div className='text-muted small mb-2'>
                          <i className='fas fa-tools me-1'></i>
                          {entry.tool?.name} | <i className='fas fa-tasks me-1'></i>
                          {entry.taskType?.name}
                          {entry.task && (
                            <>
                              {' '}
                              | <i className='fas fa-link me-1'></i>
                              {entry.task.title}
                            </>
                          )}
                        </div>
                        <p className='mb-2 small'>{entry.description}</p>
                        <div className='d-flex gap-3'>
                          <span>
                            <strong>Billable:</strong> {entry.billableHours}h
                          </span>
                          <span>
                            <strong>Actual:</strong> {entry.actualHours}h
                          </span>
                          {entry.isOvertime && (
                            <Badge bg='warning' text='dark'>
                              Overtime
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className='d-flex flex-column align-items-end gap-2'>
                        <Badge bg={getStatusColor(entry.status)}>{entry.status}</Badge>
                        {entry.status === 'DRAFT' && (
                          <div>
                            <Button
                              size='sm'
                              variant='outline-primary'
                              className='me-1'
                              onClick={() => openEntryModal(date, entry)}
                            >
                              <i className='fas fa-edit'></i>
                            </Button>
                            <Button
                              size='sm'
                              variant='outline-danger'
                              onClick={() => handleDeleteEntry(entry)}
                            >
                              <i className='fas fa-trash'></i>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      )
    }
    return weekDays
  }

  // Redirect SuperAdmins to dashboard (they'll see Master Data, Team Approvals, Reports)
  // SuperAdmins don't submit timesheets, they configure the system
  // if (roleState.roleName === 'Super Admin') {
  //   return <Navigate to='/timesheet/dashboard' replace />
  // }
  if (isSuperAdmin) {
    return <Navigate to='/timesheet/dashboard' replace />
  }
  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Body>
              <h3 className='mb-3'>
                <i className='fas fa-clock me-2'></i>
                My Timesheet
              </h3>
              {weeklyData && (
                <Row>
                  <Col md={3}>
                    <div className='text-center p-3 bg-light rounded'>
                      <h4 className='mb-0'>{weeklyData.summary.totalActual}h</h4>
                      <small className='text-muted'>Total Actual</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className='text-center p-3 bg-light rounded'>
                      <h4 className='mb-0'>{weeklyData.summary.totalBillable}h</h4>
                      <small className='text-muted'>Total Billable</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className='text-center p-3 bg-light rounded'>
                      <h4 className='mb-0'>{weeklyData.summary.entriesCount}</h4>
                      <small className='text-muted'>Entries</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className='text-center p-3 bg-light rounded'>
                      <h4 className='mb-0'>{weeklyData.summary.draftCount}</h4>
                      <small className='text-muted'>Draft</small>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Week Navigation */}
      <Row className='mb-4'>
        <Col>
          <Card>
            <Card.Body className='d-flex justify-content-between align-items-center'>
              <Button variant='outline-secondary' onClick={goToPreviousWeek}>
                <i className='fas fa-chevron-left me-2'></i>
                Previous Week
              </Button>
              <div className='text-center'>
                <h5 className='mb-0'>
                  {format(currentWeekStart, 'MMM dd')} -{' '}
                  {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM dd, yyyy')}
                </h5>
                <Button size='sm' variant='link' onClick={goToCurrentWeek}>
                  Go to Current Week
                </Button>
              </div>
              <Button variant='outline-secondary' onClick={goToNextWeek}>
                Next Week
                <i className='fas fa-chevron-right ms-2'></i>
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Week Days */}
      <Row>{loading ? <div className='text-center'>Loading...</div> : renderWeekDays()}</Row>

      {/* Entry Modal */}
      <Modal show={showEntryModal} onHide={closeEntryModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEntry ? 'Edit' : 'Add'} Timesheet Entry
            <br />
            <small className='text-muted'>{format(selectedDate, 'EEEE, MMM dd, yyyy')}</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Project Code <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.projectCodeId}
                    onChange={(e) =>
                      setFormData({ ...formData, projectCodeId: e.target.value })
                    }
                    isInvalid={!!formErrors.projectCodeId}
                  >
                    <option value=''>Select project code...</option>
                    {projectCodes.map((pc) => (
                      <option key={pc.id} value={pc.id}>
                        {pc.code} - {pc.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formErrors.projectCodeId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* {(tasks.length > 0 || projectCodes.find(p => p.id === formData.projectCodeId)?.type === 'REAL') && (
                <Col md={6}>
                  <Form.Group className='mb-3'>
                    <Form.Label>
                      Task <span className='text-danger'>*</span>
                    </Form.Label>
                    <Form.Select
                      value={formData.taskId || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, taskId: e.target.value || null })
                      }
                      isInvalid={!!formErrors.taskId}
                      disabled={tasks.length === 0}
                    >
                      <option value=''>{tasks.length === 0 ? 'No tasks assigned to you' : 'Select task...'}</option>
                      {tasks.map((task) => (
                        <option key={task.id} value={task.id}>
                          {task.title}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formErrors.taskId}
                    </Form.Control.Feedback>
                    {tasks.length === 0 && (
                      <Form.Text className="text-muted">
                        No tasks active/assigned in this project.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              )} */}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Tool <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.toolId}
                    onChange={(e) => setFormData({ ...formData, toolId: e.target.value })}
                    isInvalid={!!formErrors.toolId}
                  >
                    <option value=''>Select tool...</option>
                    {tools.map((tool) => (
                      <option key={tool.id} value={tool.id}>
                        {tool.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formErrors.toolId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Task Type <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.taskTypeId}
                    onChange={(e) => setFormData({ ...formData, taskTypeId: e.target.value })}
                    isInvalid={!!formErrors.taskTypeId}
                  >
                    <option value=''>Select task type...</option>
                    {taskTypes.map((tt) => (
                      <option key={tt.id} value={tt.id}>
                        {tt.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formErrors.taskTypeId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className='mb-3'>
              <Form.Label>
                Description <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                isInvalid={!!formErrors.description}
                placeholder='Describe the work done...'
              />
              <Form.Control.Feedback type='invalid'>
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Billable Hours <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Control
                    type='number'
                    step='0.5'
                    min='0'
                    max='9'
                    value={formData.billableHours || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value)
                      setFormData({ ...formData, billableHours: isNaN(value) ? 0 : value })
                    }}
                    isInvalid={!!formErrors.billableHours}
                    disabled={formData.isOvertime}
                  />
                  <Form.Text className='text-muted'>
                    {formData.isOvertime ? 'Overtime: Billable hours must be 0' : 'Max 9 hours per day'}
                  </Form.Text>
                  <Form.Control.Feedback type='invalid'>
                    {formErrors.billableHours}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Actual Hours <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Control
                    type='number'
                    step='0.5'
                    min='0'
                    max='24'
                    value={formData.actualHours || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value)
                      setFormData({ ...formData, actualHours: isNaN(value) ? 0 : value })
                    }}
                    isInvalid={!!formErrors.actualHours}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formErrors.actualHours}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className='mb-3'>
              <Form.Check
                type='checkbox'
                label='This is overtime work'
                checked={formData.isOvertime}
                onChange={(e) => {
                  const isOvertime = e.target.checked
                  setFormData({
                    ...formData,
                    isOvertime,
                    billableHours: isOvertime ? 0 : formData.billableHours, // Auto-set to 0 for overtime
                  })
                }}
              />
              {formData.isOvertime && (
                <Form.Text className='text-warning d-block mt-2'>
                  <i className='fas fa-info-circle me-2'></i>
                  Overtime entries must have 0 billable hours. Only actual hours will be tracked.
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeEntryModal} disabled={submitting}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSaveEntry} disabled={submitting}>
            {submitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Saving...
              </>
            ) : (
              <>
                <i className='fas fa-save me-2'></i>
                Save Entry
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default MyTimesheet

